import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Overline, Metric, Spinner, Empty, scoreColor } from "@/components/Widgets";
import { PageGuide } from "@/components/PageGuide";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area, Legend } from "recharts";
import { Bell, ShieldCheck, TrendUp, TrendDown, Warning, ArrowClockwise, Trash, Check } from "@phosphor-icons/react";

export default function Monitoring() {
  const [trend, setTrend] = useState(null);
  const [mon, setMon] = useState({ enabled: false, interval_hours: 24, last_check: null });
  const [alerts, setAlerts] = useState([]);
  const [checking, setChecking] = useState(false);

  const loadTrend = () => api.get("/reputation/trend").then(({ data }) => setTrend(data)).catch(() => setTrend({ series: [], count: 0 }));
  const loadMon = () => api.get("/monitoring").then(({ data }) => setMon(data)).catch(() => {});
  const loadAlerts = () => api.get("/alerts").then(({ data }) => setAlerts(data)).catch(() => {});

  useEffect(() => { loadTrend(); loadMon(); loadAlerts(); }, []);

  const toggleMon = async () => {
    const next = !mon.enabled;
    setMon((m) => ({ ...m, enabled: next }));
    try {
      await api.post("/monitoring", { enabled: next, interval_hours: mon.interval_hours });
      toast.success(next ? "Continuous monitoring ON — we'll re-scan and alert you automatically" : "Monitoring paused");
    } catch { setMon((m) => ({ ...m, enabled: !next })); toast.error("Could not update monitoring"); }
  };

  const setInterval = async (hours) => {
    setMon((m) => ({ ...m, interval_hours: hours }));
    try { await api.post("/monitoring", { enabled: mon.enabled, interval_hours: hours }); } catch { /* ignore */ }
  };

  const checkNow = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/monitoring/check");
      if (!data.checked) { toast.error("Run a reputation scan first so we know who to monitor."); return; }
      toast.success(data.alerts_created > 0 ? `${data.alerts_created} new alert(s) — ${data.new_negatives} new negative(s)` : `All clear — no new negatives (score ${data.score})`);
      loadTrend(); loadMon(); loadAlerts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setChecking(false); }
  };

  const markRead = async (id) => {
    setAlerts((a) => a.map((x) => (x.id === id ? { ...x, read: true } : x)));
    try { await api.patch(`/alerts/${id}`); } catch { loadAlerts(); }
  };
  const removeAlert = async (id) => {
    setAlerts((a) => a.filter((x) => x.id !== id));
    try { await api.delete(`/alerts/${id}`); } catch { loadAlerts(); }
  };

  const unread = alerts.filter((a) => !a.read).length;
  const series = trend?.series || [];
  const delta = trend?.current_score != null && trend?.start_score != null ? trend.current_score - trend.start_score : null;

  return (
    <div className="space-y-6 fade-up">
      <PageGuide title="How Monitoring & Alerts works" steps={[
        "Turn on Continuous Monitoring — we re-scan your latest subject on a schedule you choose.",
        "When a NEW negative mention or a score drop appears, we raise an alert in your inbox.",
        "Use 'Check now' to run an on-demand monitoring scan anytime.",
        "Track the sentiment & score trend below to prove the campaign is working over time.",
      ]} />

      {/* Trend */}
      <Card testid="trend-panel">
        <div className="flex items-center gap-2 mb-1">
          <TrendUp size={18} weight="fill" className="text-primary" />
          <h3 className="font-head font-bold text-lg">Reputation & Sentiment Trend</h3>
          {delta != null && (
            <span className={`ml-auto font-mono text-sm flex items-center gap-1 ${delta >= 0 ? "text-emerald-400" : "text-destructive"}`} data-testid="trend-delta">
              {delta >= 0 ? <TrendUp size={14} weight="bold" /> : <TrendDown size={14} weight="bold" />}{delta >= 0 ? "+" : ""}{delta} pts
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-4">Every scan is a datapoint. Watch the score climb and negative sentiment shrink as the rebuild takes effect.</p>
        {trend === null && <div className="py-10 flex justify-center"><Spinner label="Loading trend…" /></div>}
        {trend && series.length < 2 ? (
          <Empty>Run at least 2 reputation scans (or monitoring checks) to see the trend line.</Empty>
        ) : trend && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-5">
              <Metric label="STARTING SCORE" value={trend.start_score ?? "—"} testid="metric-start-score" />
              <Metric label="CURRENT SCORE" value={trend.current_score ?? "—"} testid="metric-current-score" />
              <Metric label="DATAPOINTS" value={trend.count} testid="metric-datapoints" />
            </div>
            <Overline className="mb-2">SCORE OVER TIME</Overline>
            <div style={{ height: 160 }}>
              <ResponsiveContainer>
                <AreaChart data={series}>
                  <defs>
                    <linearGradient id="monScore" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#E9AC0C" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#E9AC0C" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1c1c21" />
                  <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 10 }} />
                  <YAxis domain={[0, 100]} tick={{ fill: "#6b7280", fontSize: 10 }} width={24} />
                  <Tooltip contentStyle={{ background: "#0f0f0f", border: "1px solid #262626", fontSize: 12 }} />
                  <Area type="monotone" dataKey="score" stroke="#E9AC0C" strokeWidth={2.5} fill="url(#monScore)" dot={{ fill: "#9D3FFF", r: 3 }} activeDot={{ r: 5, fill: "#E9AC0C" }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <Overline className="mt-5 mb-2">SENTIMENT MIX OVER TIME</Overline>
            <div style={{ height: 160 }}>
              <ResponsiveContainer>
                <LineChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1c1c21" />
                  <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 10 }} />
                  <YAxis domain={[0, 100]} tick={{ fill: "#6b7280", fontSize: 10 }} width={24} />
                  <Tooltip contentStyle={{ background: "#0f0f0f", border: "1px solid #262626", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="positive" stroke="#22c55e" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="neutral" stroke="#13C4E8" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="negative" stroke="#DC2450" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </Card>

      {/* Continuous monitoring */}
      <Card testid="monitoring-panel">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} weight="fill" className="text-primary" />
          <h3 className="font-head font-bold text-lg">Continuous Monitoring</h3>
        </div>
        <div className="border border-border p-4 flex items-center gap-3 mb-4">
          <button type="button" onClick={toggleMon} data-testid="monitoring-toggle"
            className={`relative h-5 w-9 rounded-full transition-colors duration-200 shrink-0 ${mon.enabled ? "bg-primary" : "bg-secondary border border-border"}`}>
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all duration-200 ${mon.enabled ? "left-4" : "left-0.5"}`} />
          </button>
          <div className="flex-1">
            <div className="text-sm font-medium flex items-center gap-2">
              Auto re-scan &amp; alert {mon.enabled && <span className="text-emerald-400 text-xs">● ACTIVE</span>}
            </div>
            <div className="text-xs text-muted-foreground">We re-scan your latest subject and raise an alert whenever a new negative or score drop appears.</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Overline>CHECK EVERY</Overline>
          {[6, 12, 24, 72, 168].map((h) => (
            <button key={h} onClick={() => setInterval(h)} data-testid={`interval-${h}`}
              className={`border px-3 py-1.5 text-xs font-medium transition-colors duration-200 ${mon.interval_hours === h ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>
              {h < 24 ? `${h}h` : `${h / 24}d`}
            </button>
          ))}
          {mon.last_check && <span className="text-xs text-muted-foreground ml-auto">Last check: {new Date(mon.last_check).toLocaleString()}</span>}
        </div>
        <button onClick={checkNow} disabled={checking} data-testid="check-now-btn"
          className="w-full bg-primary text-primary-foreground font-semibold py-2.5 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50">
          {checking ? <Spinner label="Scanning the web…" /> : (<><ArrowClockwise size={16} weight="bold" /> Check now</>)}
        </button>
      </Card>

      {/* Alerts inbox */}
      <Card testid="alerts-panel">
        <div className="flex items-center gap-2 mb-4">
          <Bell size={18} weight="fill" className="text-primary" />
          <h3 className="font-head font-bold text-lg">Alerts</h3>
          {unread > 0 && <span className="bg-destructive text-white text-xs font-mono px-2 py-0.5 rounded-full" data-testid="alerts-unread">{unread} new</span>}
        </div>
        {alerts.length === 0 ? (
          <Empty>No alerts yet. When monitoring finds a new negative or a score drop, it shows up here.</Empty>
        ) : (
          <div className="space-y-2" data-testid="alerts-list">
            {alerts.map((a) => (
              <div key={a.id} data-testid={`alert-${a.id}`}
                className={`border p-3 flex items-start gap-3 ${a.read ? "border-border opacity-70" : a.severity === "high" ? "border-destructive/40 bg-destructive/5" : "border-primary/40 bg-primary/5"}`}>
                <Warning size={18} weight="fill" className={a.severity === "high" ? "text-destructive shrink-0 mt-0.5" : "text-primary shrink-0 mt-0.5"} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{a.title}</div>
                  {a.detail && <div className="text-xs text-muted-foreground mt-1">{a.detail}</div>}
                  <div className="text-[11px] text-muted-foreground mt-1 font-mono">{new Date(a.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!a.read && (
                    <button onClick={() => markRead(a.id)} data-testid={`alert-read-${a.id}`} title="Mark read"
                      className="p-1.5 text-muted-foreground hover:text-primary transition-colors duration-200"><Check size={15} weight="bold" /></button>
                  )}
                  <button onClick={() => removeAlert(a.id)} data-testid={`alert-delete-${a.id}`} title="Dismiss"
                    className="p-1.5 text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash size={15} weight="bold" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
